import {
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { TabsStateService } from '../../shared/services/tabs-state.service';
import { AllPagesImportComponent } from './all-pages-import/all-pages-import.component';
import { ApplicationStateService } from '../../shared/services/application-state.service';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { LimitToPipe } from '../../../../../../libs/shared/src/pipes/limit-character.pipe';
import { LocalDatePipe } from '@anihis/shared/src/pipes/local-date.pipe';
import { AuthenticationService } from '@anihis/shared/src/services/authentication.service';
import { NotesStore } from '../../shared/store/notes.store';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { NoteDialogComponent } from '../../shared/component/note-dialog/note-dialog.component';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { ShellStore } from '../shell.store';
import { SettingsNavigationService } from '../../settings/settings-navigation.service';
import { ClinicWelcomeScreenComponent } from './clinic-welcome-screen/clinic-welcome-screen.component';
import { tabLabelKey } from '../../shared/config/manager-portal-tabs.config';
import { formatManagerAtRiskNotification } from '../../manager/shared/manager-at-risk-notification.util';
import { AdminOrganizationDetailNavigationService } from '../../admin/shared/admin-organization-detail-navigation.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'anihis-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    AllPagesImportComponent,
    MatIconModule,
    MatDialogModule,
    MatTooltipModule,
    LimitToPipe,
    LocalDatePipe,
    TranslocoModule,
    MatButtonModule,
    MatIcon,
    MatSelectModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    ClinicWelcomeScreenComponent,
  ],
  animations: [
    trigger('opacityInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-in-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        style({ opacity: 1 }),
        animate('300ms ease-in-out', style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class NavigationComponent {
  readonly showEnvironmentBanner = environment.showEnvironmentBanner ?? false;

  private tabsStateService = inject(TabsStateService);
  private applicationStateService = inject(ApplicationStateService);
  private transloco = inject(TranslocoService);
  private authenticationService = inject(AuthenticationService);
  storeNotes = inject(NotesStore);
  private dialog = inject(MatDialog);
  private settingsNavigation = inject(SettingsNavigationService);
  private adminOrganizationNavigation = inject(AdminOrganizationDetailNavigationService);

  // Expose language as a signal (store-like) instead of an Observable used
  // directly in the template. The template can read `lang()` synchronously.
  lang = signal(
    this.transloco.getActiveLang?.() || this.applicationStateService.language,
  );

  tabs = this.tabsStateService.tabs;
  selectedIndex = this.tabsStateService.selectedIndex;
  currentTime = signal(new Date());
  isEditing = signal(false);
  userRole = this.authenticationService.getRoleFromToken();

  selectedClinicControl = new FormControl<string | null>(null);

  notificationsOpen = signal(false);
  readonly notifPanelStyle = signal<Record<string, string>>({});

  @ViewChild('notifBellBtn')
  private notifBellBtn?: ElementRef<HTMLElement>;

  notifications = signal<{ text: string; createdAt?: string }[]>([]);

  shellStore = inject(ShellStore);

  constructor() {
    // Keep currentTime ticking, but only once per minute to reduce change
    // detection overhead (navigation shows only date and HH:mm).
    const update = () => this.currentTime.set(new Date());
    const scheduleMinuteTicks = () => {
      update();
      const now = new Date();
      const msToNextMinute =
        (60 - now.getSeconds()) * 1000 - now.getMilliseconds();
      setTimeout(() => {
        update();
        setInterval(update, 60_000);
      }, msToNextMinute);
    };

    scheduleMinuteTicks();

    // Subscribe to language changes and update the local signal so the
    // template can read the language from a store-like property (no async
    // pipe in template).
    this.applicationStateService.languageChanged$.subscribe((l) => {
      // Defer updating the signal to the next microtask to avoid
      // ExpressionChangedAfterItHasBeenCheckedError when the language
      // changes during the current change-detection cycle.
      Promise.resolve().then(() => this.lang.set(l));
    });

    this.transloco.langChanges$.subscribe((l) => {
      Promise.resolve().then(() => this.lang.set(l));
    });
  }

  openNoteDetail(note: any) {
    this.dialog.open(NoteDialogComponent, {
      data: note,
      width: '560px',
      panelClass: 'anihis-note-dialog-panel',
    });
  }

  onTabClick(index: number): void {
    this.preSelected(index);
  }

  middleClickEvent(event: MouseEvent, tab: string, index?: number): void {
    if (event.which === 2) {
      const tabsBeforeClose = this.tabs();
      const closedIndex =
        typeof index === 'number' ? index : tabsBeforeClose.indexOf(tab);
      const currentActive = this.selectedIndex();

      this.tabsStateService.removeTab(tab);

      const newActiveIndex = this.getIndexAfterClose(
        closedIndex,
        currentActive,
        this.tabs().length,
      );
      this.preSelected(newActiveIndex);
    }
  }

  deleteTab(event: MouseEvent, tab: string, _index?: number): void {
    event.stopPropagation();

    const tabsBeforeClose = this.tabs();
    const closedIndex =
      typeof _index === 'number' ? _index : tabsBeforeClose.indexOf(tab);
    const currentActive = this.selectedIndex();

    this.tabsStateService.removeTab(tab);

    const newActiveIndex = this.getIndexAfterClose(
      closedIndex,
      currentActive,
      this.tabs().length,
    );
    this.preSelected(newActiveIndex);
  }

  closeAllTabs(event?: Event): void {
    event?.stopPropagation();
    this.tabsStateService.closeAllTabs();
  }

  showCloseAllTabsButton(): boolean {
    return this.tabs().length > 2;
  }

  showClinicWelcomeScreen(): boolean {
    return this.userRole === 'Clinic' && this.tabs().length === 0;
  }

  private getIndexAfterClose(
    closedIndex: number,
    currentActive: number,
    remainingCount: number,
  ): number {
    if (remainingCount <= 0) {
      return 0;
    }

    // Closing a tab before the active one shifts active index left by 1.
    if (closedIndex < currentActive) {
      return Math.max(0, currentActive - 1);
    }

    // Closing a tab after the active one keeps active index unchanged.
    if (closedIndex > currentActive) {
      return Math.min(currentActive, remainingCount - 1);
    }

    // Closing the active tab.
    const wasLastTabBeforeClose = closedIndex === remainingCount;

    // If active last tab is closed, jump to the first tab.
    if (wasLastTabBeforeClose) {
      return 0;
    }

    // Otherwise choose previous tab.
    return Math.max(0, closedIndex - 1);
  }

  preSelected(index: number): void {
    const newIndex = index === -1 ? 0 : index;
    const prevIndex = this.selectedIndex();

    // If the index didn't change, don't trigger refreshes or update state.
    if (newIndex === prevIndex) {
      setTimeout(() => {
        this.scrollToActiveTab();
      }, 100);
      return;
    }

    this.tabsStateService.updateState(newIndex);
    // Notify children that this tab became active so they can refresh data.
    const tab = this.tabs()[newIndex];
    if (tab) this.tabsStateService.triggerRefresh(tab);

    setTimeout(() => {
      this.scrollToActiveTab();
    }, 100);
  }

  onTabChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const index = parseInt(target.value, 10);
    if (!isNaN(index)) {
      this.preSelected(index);
    }
  }

  startEditing() {
    this.isEditing.set(true);
    setTimeout(() => {
      const input = document.getElementById('note-input');
      input?.focus();
    }, 0);
  }

  stopEditing() {
    this.isEditing.set(false);
  }

  toggleNotifications() {
    const opening = !this.notificationsOpen();
    this.notificationsOpen.set(opening);
    if (opening) {
      this.shellStore.markAllRead();
      queueMicrotask(() => this.updateNotificationPanelPosition());
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.notificationsOpen()) {
      this.updateNotificationPanelPosition();
    }
  }

  onNavigationScroll(): void {
    if (this.notificationsOpen()) {
      this.updateNotificationPanelPosition();
    }
  }

  private updateNotificationPanelPosition(): void {
    const el = this.notifBellBtn?.nativeElement;
    if (!el) {
      return;
    }

    const rect = el.getBoundingClientRect();
    const width = Math.min(320, Math.max(240, window.innerWidth - 24));
    let left = rect.right - width;
    left = Math.max(12, Math.min(left, window.innerWidth - width - 12));

    this.notifPanelStyle.set({
      position: 'fixed',
      top: `${Math.round(rect.bottom + 8)}px`,
      left: `${Math.round(left)}px`,
      width: `${width}px`,
    });
  }

  onNotificationClick(notification: {
    uid?: string;
    type?: string;
    billingUid?: string;
    clinicUid?: string;
    entityUid?: string;
    entityType?: string;
  }) {
    if (this.opensManagerPremiumPackage(notification.type)) {
      if (notification.uid) {
        this.shellStore.markNotificationRead(notification.uid);
      }
      this.adminOrganizationNavigation.open(
        notification.entityType,
        notification.entityUid,
      );
      this.notificationsOpen.set(false);
      setTimeout(() => this.scrollToActiveTab(), 200);
      return;
    }

    if (this.opensAppointmentRequests(notification.type)) {
      if (notification.uid) {
        this.shellStore.markNotificationRead(notification.uid);
      }
      this.tabsStateService.createOpenTabs('Dashboard');
      this.notificationsOpen.set(false);
      setTimeout(() => this.scrollToActiveTab(), 200);
      return;
    }

    if (this.opensAtRiskContracts(notification.type)) {
      if (notification.uid) {
        this.shellStore.markNotificationRead(notification.uid);
      }
      this.tabsStateService.createOpenTabs('At-risk contracts');
      this.notificationsOpen.set(false);
      setTimeout(() => this.scrollToActiveTab(), 200);
      return;
    }

    if (!this.opensPremiumInvoices(notification.type)) {
      return;
    }

    this.settingsNavigation.openPremiumInvoices(
      notification.billingUid ?? null,
      this.resolveNotificationClinicUid(notification),
      notification.type === 'subscription-warning' ? { paid: 'unpaid' } : null,
    );
    if (notification.uid) {
      this.shellStore.markNotificationRead(notification.uid);
    }
    this.tabsStateService.createOpenTabs('Settings');
    this.notificationsOpen.set(false);
    setTimeout(() => this.scrollToActiveTab(), 200);
  }

  opensPremiumInvoices(type?: string): boolean {
    return (
      type === 'premium-billing' ||
      type === 'premium-billing-deadline' ||
      type === 'subscription-warning'
    );
  }

  private resolveNotificationClinicUid(notification: {
    uid?: string;
    clinicUid?: string;
  }): string | null {
    if (notification.clinicUid) {
      return notification.clinicUid;
    }

    const uid = notification.uid ?? '';
    for (const prefix of ['sub-warning-', 'sub-overdue-', 'sub-blocked-']) {
      if (uid.startsWith(prefix)) {
        return uid.slice(prefix.length);
      }
    }

    return null;
  }

  opensAtRiskContracts(type?: string): boolean {
    return type === 'manager-contract-at-risk';
  }

  opensManagerPremiumPackage(type?: string): boolean {
    return type === 'manager-premium-package';
  }

  /** Only the clinic dashboard hosts the request review panel. */
  opensAppointmentRequests(type?: string): boolean {
    return type === 'appointment-request' && this.userRole === 'Clinic';
  }

  notificationText(notification: {
    text: string;
    type?: string;
    managerAtRisk?: {
      name: string;
      riskReason?: string;
      daysUntilExpiry?: number;
    };
  }): string {
    if (notification.type === 'manager-contract-at-risk' && notification.managerAtRisk) {
      return formatManagerAtRiskNotification(
        this.transloco,
        notification.managerAtRisk,
      );
    }

    return notification.text;
  }

  isClickableNotification(type?: string): boolean {
    return (
      this.opensPremiumInvoices(type) ||
      this.opensAtRiskContracts(type) ||
      this.opensManagerPremiumPackage(type) ||
      this.opensAppointmentRequests(type)
    );
  }

  isPremiumNotification(type?: string): boolean {
    return this.opensPremiumInvoices(type);
  }

  dismissNotification(event: Event, uid?: string) {
    event.stopPropagation();
    if (!uid) return;
    this.shellStore.dismissNotification(uid);
  }

  scrollToActiveTab(): void {
    requestAnimationFrame(() => {
      const container = document.getElementById('tabNav');
      if (!container) {
        return;
      }

      const activeTab = container.querySelector(
        '.active-tab-background.active-tab-text',
      ) as HTMLElement;
      if (!activeTab) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();
      const tabCenter = tabRect.left - containerRect.left + tabRect.width / 2;
      const desiredScrollLeft = tabCenter - container.clientWidth / 2;

      container.scrollTo({
        left: Math.max(0, desiredScrollLeft),
        behavior: 'smooth',
      });
    });
  }

  tabLabel(tab: string): string {
    return tabLabelKey(tab);
  }

  tabIcon(tab: string): string {
    const map: Record<string, string> = {
      Clinics: 'clinic-icon',
      Ambulances: 'clinic-icon',
      Hotels: 'hotel-icon',
      Others: 'other-icon',
      Clubs: 'clubs-icon',
      Walkers: 'walk-icon',
      Stores: 'stores-icon',
      'Pet Insurances': 'pet-insurance-icon',
      Promotions: 'promotions-icon',
      Trainers: 'users-icon',
      Groomers: 'scissors-icon',
      'Pet Transporters': 'car-icon',
      Services: 'type-services-icon',
      Cards: 'file-text-icon',
      Veterinarians: 'user-icon',
      Debts: 'credit-card-icon',

      ClinicNotes: 'note-icon',
      OwnerNotes: 'note-icon',
      'All Reports': 'my-reports-icon',

      'Manufacturers of medicaments': 'manufacturers-medicaments-icon',
      Suppliers: 'manufacturers-medicaments-icon',
      'Serial number confirmation': 'serial-number-icon',
      Medicaments: 'type-medicaments-icon',
      Diagnoses: 'types-diagnoses-icon',
      'System-Diagnoses': 'types-diagnoses-icon',
      'System-Services': 'type-services-icon',
      'System-Type-Medicaments': 'type-medicaments-icon',
      'System-Notifications': 'notification-icon',
      'Legal-Documents': 'file-icon',
      'Video-Tutorials': 'book-icon',
      'Subscription-Plans': 'type-services-icon',

      Breeds: 'dog-icon',
      Interesting: 'interesting-icon',
      Events: 'calendar-white-icon',
      'Forum-Topics': 'interesting-icon',
      Tips: 'interesting-icon',
      Nutrition: 'nutrition-icon',
      'First Aid': 'first-aid-icon',
      News: 'newspaper-icon',
      Dashboard: 'dashboard',
      'Managers to pay': 'users-icon',
      'Unassigned contracts': 'users-icon',
      'At-risk contracts': 'notification-icon',
      'Manager organizations': 'clinic-icon',
      'Create organization': 'add-white-icon',
      'Manager payouts': 'arrow-payment-history-white',
      'Manager hierarchy': 'protocol-icon',
      'Manager audit log': 'file-icon',
      'Manager sub-managers': 'users-icon',
      'Manager notes': 'note-icon',
      Settings: 'settings-icon',
      Support: 'help-shell-icon',
      Owners: 'user-icon',
    };

    return map[tab] ?? 'book-icon';
  }
}
